<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-management-team-create-user">
    <div class="div-header">
      <div class="div-title">Add a team member</div>
      <div class="div-close">
        <span class="wy-blue-button close wy-app-modal-header-close el-icon-close" size="mini" @click="handleClose"></span>
      </div>
    </div>
    <div class="steps-content">
      <el-steps :active="activeStep" align-center finish-status="success">
        <el-step v-bind:class="{'filled': isStep1Filled}" title="Info" description=""></el-step>
        <el-step v-bind:class="{'filled': isStep2Filled}" title="User type" description=""></el-step>
        <el-step v-bind:class="{'filled': isStep3Filled}" title="Permissions" description=""></el-step>
        <el-step v-bind:class="{'filled': isStep4Filled}" title="Invite" description=""></el-step>
      </el-steps>
    </div>

    <div v-show="activeStep==0" class="div-content-title">
      Add a new user to your {{$t('app.name')}} account with an email address.
    </div>
    <div v-show="activeStep==0" class="div-content-step1-section">
      <el-form class="form" ref="userInforForm" :model="userFormData" :rules="userFormRules" :hide-required-asterisk="true">
        <div class="div-content-name-section">
          <el-form-item class="div-name-input" :label="$t('account.firstName.label')" prop="firstName">
            <el-input v-model="userFormData.firstName" ref="firstNameInput" :placeholder="$t('account.firstName.placeholder')" size="medium"></el-input>
          </el-form-item>
        </div>
        <div class="div-content-name-section">
          <el-form-item class="div-name-input" :label="$t('account.lastName.label')" prop="lastName">
            <el-input v-model="userFormData.lastName" :placeholder="$t('account.lastName.placeholder')" size="medium"></el-input>
          </el-form-item>
        </div>
        <div class="div-content-email-section">
          <el-form-item class="div-email-input" :label="$t('account.email.label')" prop="email">
            <el-input v-model="userFormData.email" :placeholder="$t('account.email.placeholder')" size="medium"></el-input>
          </el-form-item>
        </div>
      </el-form>
    </div>
    <div v-show="activeStep==1" class="div-content-user-type-section">
      <div class="div-user-name-setion">
        <div class="div-account-icon"><i class="nav-el-icon el-icon-account"></i></div>
        <div class="div-user-info">
          <div class="div-account-user-name">{{userFormData.firstName}}</div>
          <div class="div-account-email" :title="userFormData.email">{{userFormData.email}}</div>
        </div>
      </div>

      <div class="div-user-type-setion">
        <div class="div-user-type-item" label="REGULAR">
          <el-radio v-model="userFormData.userType" label="REGULAR">Internal User</el-radio>
          <div class="div-description">
            User logs in through Wysdom VPN
          </div>
        </div>

        <div class="div-user-type-item" label="CLOUD">
          <el-radio v-model="userFormData.userType" label="CLOUD">Cloud User</el-radio>
          <div class="div-description">
            User logs in through Cloudflare
          </div>
        </div>

      </div>
    </div>
    <div v-show="activeStep==2" class="div-content-step2-section">
      <div class="div-user-name-setion">
        <div class="div-account-icon"><i class="nav-el-icon el-icon-account"></i></div>
        <div class="div-user-info">
          <div class="div-account-user-name">{{userFormData.firstName}}</div>
          <div class="div-account-email" :title="userFormData.email">{{userFormData.email}}</div>
        </div>
      </div>
      
      <div class="div-step2-permission-setion">
        <div class="div-permission-item" v-for="role in appRoles" :label="role.label" :key="role.key">
          <el-radio v-model="selectedRole" :label="role.key">{{role.label}}</el-radio>
          <div class="div-description">
            {{role.description}}
          </div>
        </div>
      </div>
    </div>
    <div v-show="activeStep==3" class="div-content-step3-section">
      <div class="div-user-name-setion">
        <div class="div-account-icon"><i class="nav-el-icon el-icon-account"></i></div>
        <div class="div-user-info">
          <div class="div-account-user-name">{{userFormData.firstName}}</div>
          <div class="div-account-email" :title="userFormData.email">{{userFormData.email}}</div>
        </div>
      </div>
      <div class="div-invite-msg">
        Invite your teammate to {{$t('app.name')}}
      </div>
      <div class="div-sent-invitation-msg">
        Send them an invitation with everything they need to get started.
      </div>
    </div>
    <div class="div-steps-footer">
      <el-button v-if="showPreBtn" class="wy-blue-button pre" size="mini" @click="handlePre">Back</el-button>
      <el-button v-if="!showSendBtn" ref="nextBtn" class="wy-blue-button next" size="mini" @click="handleNext">Next</el-button>
      <el-button v-if="showSendBtn" ref="sendBtn" class="wy-blue-button send" size="mini" :loading="loading" @click="handleSendUserInvitaton">Send</el-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';

export default Vue.extend({
  name: 'wy-management-team-create-user',
  props: ["controller"],
  data() {
    return {
      loading: false,
      activeStep: 0,
      showPreBtn: false,
      showSendBtn: false,
      userFormData: {
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        userType: 'REGULAR'
      },
      selectedRole: "USER",
      // selectedUserType: "internalUser",

      userFormRules: {
        firstName: [
          { required: true, message: this.$t('account.firstName.validateEmpty'), trigger: ['blur', 'change'] }
        ],
        lastName: [
          { required: true, message: this.$t('account.lastName.validateEmpty'), trigger: ['blur', 'change'] }
        ],
        email: [
          { required: true, message: this.$t('account.email.emailRequired'), trigger: ['blur', 'change'] },
          { type: 'email', message: this.$t('account.email.emailInvalid'), trigger: ['blur'] }
        ]
      },
    }
  },

  computed: {
    ...mapGetters(['authUser', 'appRoles', 'accessHost']),
  },

  methods: {
    ...mapActions(['inviteUser']),

    handleClose() {
      this.controller.showUserSteps = false;
    },
    async handleNext() {
      if(this.activeStep == 0) {
        this.$refs.userInforForm.validate(async (isValid) => {
          if(isValid) {
            this.showNextBtn = true;
            this.activeStep = 1
            this.showPreBtn = true;
          }
        })
      }
      else if(this.activeStep == 1) {
        this.activeStep = 2;    
      } 
      else if(this.activeStep == 2){
        this.activeStep = 3;
      }
      if(this.activeStep == 3) {
        this.showSendBtn = true;
        
        setTimeout(()=>{
          this.$refs.sendBtn.$el.focus()
        }, 0)
      }
    },

    handlePre() {
      if(this.activeStep == 2) {
        this.activeStep = 1;
      }
      else {
        if(this.activeStep == 1) {
          this.activeStep = 0;
        }
        if(this.activeStep == 3){
          this.activeStep = 2;
        }
      }
      if(this.activeStep == 0) {
        this.showPreBtn = false;
        setTimeout(()=>{
          this.$refs.firstNameInput.focus();
        }, 300)
      }
      this.showSendBtn = false;
    },

    async handleSendUserInvitaton() {
      if(this.activeStep == 3) {
        this.loading = true;
        
        let permissions = this.appRoles.find(el => el.key == this.selectedRole).permissions;        
        let payload = {
          "accountId": this.authUser.company.accountId,
          "role" : this.selectedRole,
          "userType": this.userFormData.userType,
          "name" : `${this.userFormData.firstName}, ${this.userFormData.lastName}`,
          "firstName" : this.userFormData.firstName,
          "lastName" : this.userFormData.lastName,
          "email": this.userFormData.email,
          "mobileNumber" : "",
          "timezone" : "UTC",
          "fullUrl" : `${this.accessHost(this.userFormData.userType)}/studio/registration`,
          "accessList": [		
            {
              "companyId": this.authUser.company.id,
              "permissions": permissions
            }
          ]
        }
        let res = await this.inviteUser(payload)
        if(res.status == 200){
          this.controller.loadUsers();
          this.$refs.userInforForm.resetFields();
          this.controller.showUserSteps = false;
          this.$store.dispatch('amplitudeLog', {name: 'new_user_added', data: {details: payload}});
        }
        else if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null, `errorCode.code${res.data.errorCode}`);
        }
        else {
          this.$root.showNotificationLocale("error", 'error.serverErrorTitle', 'error.serverErrorMsg');
        }
        
        setTimeout(() => {
          this.loading = false;
        }, 5000)
      }
    }
  },

  created() {
    this.isStep1Filled = false;
    this.isStep2Filled = false;
    this.isStep3Filled = false; 
    this.isStep4Filled = false;
  },

  mounted() {
    this.$refs.firstNameInput.focus();
  },

  beforeDestroy() {
    this.controller.$refs.addMemberBtn.$el.focus();
  }
});
</script>

<style lang="scss">
.wy-management-team-create-user {
  padding: 10px;
  .el-icon-close:before {
    content: "\E6DB";
    font-weight: 600 !important;
    position: relative;
    right: 10px;
    top: -8px;
  }
  .div-header {
    height: 50px;
    padding: 0px 0px 0px 20px;
  }
  .div-title {
    display: inline-block;
    padding: 10px 10px 0px 10px;
    font-size: 16px;
    font-weight: 600;
  }
  .div-close {
    float: right;
    padding: 0px 0px 0px 0px;
    .el-button.close {
      background-color: #FFF;
      -webkit-box-shadow: unset;
      box-shadow: unset;
      color: var(--site-font-primary-color);
      font-size: 15px;
      padding: 7px 20px 7px 20px;
    }
    .el-button.close:focus {
      outline: none;
      box-shadow: 0px 0px 10px #0a21f3a3;
    }
  }
  .el-steps {
    padding: 10px 30px 30px 30px;
    .el-step{
      .el-step__head.is-success {
        border-color: #10A29B;
        .el-step__icon.is-text {
          border: 0;
          background-color: #10A29B;
          color: #fff;
          cursor: pointer;
        }
      }
      .el-step__title.is-success {
        color: #10A29B;
      }

      .el-step__head.is-process {
        border-color: #214586;

        .el-step__icon.is-text {
          border: 0;
          background-color: #214586;
          color: #fff;
        }
      }
      .el-step__title.is-process {
        color: #214586;
      }

      .el-step__head.is-wait {
        border-color: var(--site-font-primary-color);

        .el-step__icon.is-text {
          border: 2;
          border-color: var(--site-font-primary-color);
          background-color: #fff;
          color: var(--site-font-primary-color);
        }
      }
      .el-step__title.is-wait {
        color: var(--site-font-primary-color);
      }
    }

    .el-step.filled {
      .el-step__icon.is-text {
        cursor: pointer;
      }
    }
  }
  
  .div-content-title {
      padding: 0px 60px 20px 172px;
  }

  .div-user-name-setion {
      text-align: center;
      padding-right: 80px;
      padding-bottom: 35px;
      .div-user-info {
          display: inline-block;
          height: 40px;
          vertical-align: bottom;
          .div-account-user-name {
              text-align: left;
              font-size: 18px;
              font-weight: 500;
              padding-bottom: 5px;
          }
          .div-account-email {
            text-align: left;
            font-size: 12px;
            max-width: 250px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
      }
      
      .div-account-icon{
          padding-right: 25px;
          display: inline-block;
          height: 43px;
          .el-icon-account {
              width: 25px;
              height: 25px;
              cursor: pointer;
              margin-bottom: 0px;
              background-size: 100%;
              background-image: url('../../assets/icons/icon_profile.svg');
          }
      }
  }

  .div-content-step1-section {
    height: 120px;
    padding: 10px 60px 40px 73px;
    .el-form-item__content {
      padding-right: 40px;
      width: 200px;
    }    
    .el-form-item__label {
      font-weight: 500;
      color: var(--site-font-primary-color);
    }
    .div-content-name-section {
      display: inline-block;      
    }
    .div-content-email-section {
      display: inline-block;
      .el-form-item__content {
        padding-right: 0px;
        width: 280px;
      }
    }

    .div-account-icon {
      display: inline-block;
    }
    .el-input input{
      border: 2px solid #E8E8E8;
      border-radius: 5px;
      // border-bottom-width: 1px !important;
    }
    .el-input input:focus {
      border: 2px solid #005EB8;
      border-radius: 5px;
      // border-bottom-width: 2px !important;
    }
    .el-form-item.is-error .el-input__inner, .el-form-item.is-error .el-input__inner:focus {
      border-color: #F56C6C !important;
    }
    input.el-input__inner {
      border-width: 2px !important;
      padding-left: 10px;
      color: var(--site-font-primary-color);
    }
  }
  .div-content-user-type-section {
    height: 157px;
    padding: 10px 60px 40px 129px;
    .div-user-type-setion {
      display: flex;
      justify-content: center;
      .div-user-type-item {
        display: inline-block;
        width: 215px;
      }
      .div-description {
        font-size: 10px;
        text-align: left;
        padding-top: 5px;
        padding-left: 25px;
      }
      .el-radio {
        color: var(--site-font-primary-color);
      }
      .el-radio__input.is-checked .el-radio__inner {
        border-color: #214586;
        background: #214586;
      }
      .el-radio__input.is-checked+.el-radio__label {
        color: #214586;
      }
    }
  }

  .div-content-step2-section {
    height: 157px;
    padding: 10px 60px 40px 73px;
    .div-step2-permission-setion {
      .div-permission-item {
        display: inline-block;
        width: 215px;
        padding-right: 40px;
      }
      .div-description {
        font-size: 10px;
        text-align: left;
        padding-top: 5px;
        padding-left: 25px;
      }
      .el-radio {
        color: var(--site-font-primary-color);
      }
      .el-radio__input.is-checked .el-radio__inner {
        border-color: #214586;
        background: #214586;
      }
      .el-radio__input.is-checked+.el-radio__label {
        color: #214586;
      }
    }
  }

  .div-content-step3-section {
    height: 157px;
    padding: 10px 60px 40px 73px;

    .div-invite-msg {
      text-align: center;
      width: 200px;
      padding-left: 257px;
      font-weight: 500;
      padding-bottom: 20px;
    }
    .div-sent-invitation-msg {
      width: 240px;
      padding-left: 240px;
      padding-bottom: 20px;
      text-align: center;
    }
  }

  .div-steps-footer {
    padding: 20px 30px 40px 30px;
    border-top: 1px solid #B3B3B3;

    .el-button.pre {
      float: left;
      background-color: #FFF;
      -webkit-box-shadow: unset;
      box-shadow: unset;
      color: var(--site-font-primary-color);
      padding: 7px 30px 7px 30px;
      box-shadow: 0px 3px 6px #00000029;
      border: 3px solid #214586;
    }
    .el-button.next {
      float: right;
      padding: 10px 30px 10px 30px;
    }
    .el-button.next:focus {
      outline: none;
      box-shadow: 0px 0px 10px #0a21f3a3;
    }
    .el-button.pre:focus {
      outline: none;
      box-shadow: 0px 0px 10px #0a21f3a3;
    }
    .el-button.send {
      float: right;
      padding: 10px 30px 10px 30px;
    }
    .el-button.send:focus {
      outline: none;
      box-shadow: 0px 0px 10px #0a21f3a3;
    }
  }
}
</style>